import { MicOff } from '@mui/icons-material';
import { IconButton, Tooltip, Typography, Stack } from '@mui/material';
import React, { useMemo } from 'react';
import { useMicrophonePermission } from 'app/hooks/useMicrophonePermission';
import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/i18n/types/i18nNamespace';
import type { I18nScenarioEditorNs, I18nCommonNs } from '@/i18n/dictionaries/interfaces';

function getBrowserHelpLink(): string {
  const ua = navigator.userAgent;
  // Default to Chrome guide
  let link = 'https://support.google.com/chrome/answer/2693767?hl=en&co=GENIE.Platform%3DDesktop';
  if (/Edg\//.test(ua)) {
    link =
      'https://support.microsoft.com/en-us/windows/windows-camera-microphone-and-privacy-a83257bc-e990-d54a-d212-b5e41beba857';
  } else if (/Firefox\//.test(ua)) {
    link = 'https://support.mozilla.org/en-US/kb/how-manage-your-camera-and-microphone-permissions';
  } else if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) {
    link = 'https://support.apple.com/guide/safari/websites-ibrwe2159f50/mac';
  }
  return link;
}

export const MicrophonePermissionIndicator: React.FC = () => {
  const { permission, hasMicrophone, refresh } = useMicrophonePermission();
  const [tScenario] = useTranslation([I18nNamespace.ScenarioEditor]);
  const [tCommon] = useTranslation([I18nNamespace.Common]);

  const showWarning = useMemo(() => {
    // Show only when explicitly denied or no microphone detected
    if (hasMicrophone === false) return true;
    return permission === 'denied';
  }, [permission, hasMicrophone]);

  const tooltip = useMemo(() => {
    if (hasMicrophone === false) {
      return tScenario(nameof.full<I18nScenarioEditorNs>((n) => n.useScenarioIntro.microphoneNotDetected));
    }
    if (permission === 'denied') {
      return tCommon(nameof.full<I18nCommonNs>((n) => n.audioSettings.noMicrophoneFound));
    }
    return '';
  }, [permission, hasMicrophone, tScenario, tCommon]);

  const handleClick = async () => {
    if (!showWarning) return;

    // If the browser can prompt (permission is 'prompt' or unknown),
    // try requesting mic access again via getUserMedia on user gesture.
    const canAttemptPrompt = permission === 'prompt' || permission === 'unknown' || permission === 'unsupported';

    if (navigator?.mediaDevices?.getUserMedia && canAttemptPrompt) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        // Immediately stop tracks; we only needed the permission.
        stream.getTracks().forEach((t) => t.stop());
        refresh();
        return;
      } catch {
        // Fall through to help link
      }
    } else if (navigator?.mediaDevices?.getUserMedia) {
      // Even if state says denied, user may have changed settings; try once.
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach((t) => t.stop());
        refresh();
        return;
      } catch {
        // Fall through to help link
      }
    }

    const link = getBrowserHelpLink();
    window.open(link, '_blank', 'noopener,noreferrer');
  };

  if (!showWarning) return null;

  return (
    <Tooltip title={tooltip} placement="bottom" enterTouchDelay={0}>
      <Stack alignItems="center" spacing={0.25} sx={{ mr: 1 }}>
        <IconButton
          size="large"
          color="error"
          onClick={handleClick}
          aria-label="microphone-permission-warning"
          sx={{ p: 0.5 }}
        >
          <MicOff sx={{ fontSize: 28 }} />
        </IconButton>
        <Typography variant="caption" color="error" sx={{ lineHeight: 1.2, textAlign: 'center', maxWidth: 160 }}>
          {tScenario(nameof.full<I18nScenarioEditorNs>((n) => n.useScenarioIntro.microphoneRequiredToPlay))}
        </Typography>
      </Stack>
    </Tooltip>
  );
};

export default MicrophonePermissionIndicator;
